import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const SHAPES = ["Oval", "Round", "Square", "Heart", "Rectangle"];
const BRIDGES = ["Wide", "Medium", "Standard"];

export default function AppFitProfile() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [shape, setShape] = useState(user?.fit_profile?.face_shape || "");
  const [bridge, setBridge] = useState(user?.fit_profile?.nose_bridge || "");
  const [scanning, setScanning] = useState(false);
  const [recs, setRecs] = useState([]);

  const startScan = async () => {
    if (!user) {
      toast("Sign in to save your fit profile");
      navigate("/login");
      return;
    }
    setScanning(true);
    setTimeout(() => {
      setShape((s) => s || SHAPES[Math.floor(Math.random() * SHAPES.length)]);
      setBridge((b) => b || "Wide");
      setScanning(false);
      setStep(1);
    }, 2200);
  };

  const save = async () => {
    if (!user) {
      toast("Sign in to save your fit profile");
      navigate("/login");
      return;
    }
    try {
      await api.put("/me/fit-profile", { face_shape: shape.toLowerCase(), nose_bridge: bridge.toLowerCase() });
      const r = await api.get("/me/recommendations");
      setRecs(r.data || []);
      await refresh();
      setStep(2);
      toast.success("Fit profile saved");
    } catch {
      toast.error("Could not save");
    }
  };

  return (
    <div data-testid="app-fitprofile" className="pb-10">
      <header className="px-5 pt-12 pb-3">
        <div className="text-[11px] tracking-luxury uppercase text-poche-gold">My Fit Profile</div>
        <h1 className="font-serif text-2xl mt-1">Find your contour.</h1>
      </header>

      {step === 0 && (
        <div className="px-5">
          <div className="bg-poche-ink border border-poche-gold/30 aspect-[3/4] relative overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
              {scanning ? (
                <div className="text-center">
                  <div className="w-20 h-20 border-2 border-poche-gold/40 rounded-full mx-auto relative">
                    <div className="absolute inset-0 border-2 border-poche-gold rounded-full animate-ping" />
                  </div>
                  <div className="text-[10px] tracking-luxury uppercase text-poche-gold mt-4">Mapping contour…</div>
                </div>
              ) : (
                <div className="text-center px-6">
                  <Sparkles size={28} className="text-poche-gold mx-auto" strokeWidth={1.4} />
                  <h2 className="font-serif text-2xl mt-3">AI Face Scan</h2>
                  <p className="text-xs text-white/55 mt-2">Hold camera at eye level. We will detect your face shape and recommend frames built for your contour.</p>
                </div>
              )}
            </div>
          </div>
          <button data-testid="start-scan" onClick={startScan} disabled={scanning} className="btn-gold w-full justify-center mt-5 disabled:opacity-60">{scanning ? "Scanning…" : "Start Face Scan"}</button>
          <button onClick={() => setStep(1)} className="w-full text-center text-[10px] tracking-luxury uppercase text-white/45 mt-3">Or enter manually</button>
        </div>
      )}

      {step === 1 && (
        <div className="px-5">
          <div className="text-[10px] tracking-luxury uppercase text-white/55">Face Shape</div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {SHAPES.map((s) => (
              <button key={s} data-testid={`shape-${s.toLowerCase()}`} onClick={() => setShape(s)} className={`p-3 border text-[11px] tracking-luxury uppercase ${shape === s ? "border-poche-gold bg-poche-gold/10 text-poche-gold" : "border-white/15 text-white/65"}`}>
                {shape === s && <Check size={12} className="inline mr-1" />}
                {s}
              </button>
            ))}
          </div>
          <div className="text-[10px] tracking-luxury uppercase text-white/55 mt-6">Nose Bridge</div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {BRIDGES.map((b) => (
              <button key={b} data-testid={`bridge-${b.toLowerCase()}`} onClick={() => setBridge(b)} className={`p-3 border text-[11px] tracking-luxury uppercase ${bridge === b ? "border-poche-gold bg-poche-gold/10 text-poche-gold" : "border-white/15 text-white/65"}`}>
                {b}
              </button>
            ))}
          </div>
          <button data-testid="save-fit" disabled={!shape || !bridge} onClick={save} className="btn-gold w-full justify-center mt-8 disabled:opacity-60">Save Fit Profile</button>
        </div>
      )}

      {step === 2 && (
        <div className="px-5">
          <div className="bg-poche-gold/10 border border-poche-gold/30 p-5">
            <div className="text-[10px] tracking-luxury uppercase text-poche-gold">Your Contour</div>
            <h2 className="font-serif text-2xl mt-1">{shape} · {bridge} Bridge</h2>
            <p className="text-xs text-white/65 mt-2">Engineered for your architecture. Bridge width 16–18mm. Sizes 145–149mm recommended.</p>
          </div>
          <div className="text-[10px] tracking-luxury uppercase text-white/45 mt-6 mb-2">Recommended Frames</div>
          <div className="grid grid-cols-2 gap-3">
            {recs.slice(0, 4).map((p) => (
              <div key={p.product_id}>
                <div className="bg-poche-ivory aspect-square overflow-hidden">
                  <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="font-serif text-sm mt-1">{p.subtitle}</div>
                <div className="text-poche-gold text-xs">${p.price}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
